import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { toast } from "sonner";
import { Download, RefreshCw, Calendar as CalendarIcon, Layers } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const COLORS = [
  "#2E5C46", "#4A8F76", "#78B89A", "#FFB347", "#C77E0F",
  "#A6D4C3", "#FF6B6B", "#5C8FCF", "#9B72CF", "#D4A574",
];

const fmtDays = (n) => `${(Number(n) || 0).toFixed(2)}d`;
const fmtHours = (n) => `${(Number(n) || 0).toFixed(1)}h`;

export const EffortOverview = () => {
  const { token } = useAuth();
  const [period, setPeriod] = useState("month");
  const [count, setCount] = useState(6); // months_back or weeks_back
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === "month"
        ? { period: "month", months_back: count }
        : { period: "week", weeks_back: count };
      const res = await axios.get(`${API}/timesheets/effort-overview`, { headers, params });
      setData(res.data);
    } catch {
      toast.error("Failed to load effort overview");
    } finally { setLoading(false); }
  }, [token, period, count]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Build flat chart data: stacked bar per bucket, one bar per project (top 8)
  const topProjects = (data?.totals?.by_project || []).slice(0, 8);
  const topProjectIds = topProjects.map((p) => p.project_id);

  const chartData = (data?.buckets || []).map((b) => {
    const row = { label: b.label };
    for (const proj of b.projects) {
      if (topProjectIds.includes(proj.project_id)) {
        row[proj.project_name] = proj.days;
      }
    }
    // bucket sub-total of "other" projects
    const otherDays = b.projects
      .filter((p) => !topProjectIds.includes(p.project_id))
      .reduce((s, p) => s + (p.days || 0), 0);
    if (otherDays > 0) row["Other"] = +otherDays.toFixed(2);
    return row;
  });

  const projectKeysForChart = [
    ...topProjects.map((p) => p.project_name),
    ...(chartData.some((r) => "Other" in r) ? ["Other"] : []),
  ];

  const exportCSV = () => {
    if (!data) return;
    const lines = [];
    lines.push(`Effort Overview — ${period === "month" ? "Monthly" : "Weekly"}`);
    lines.push(`Range: ${data.range?.start} to ${data.range?.end}`);
    lines.push("");
    // Pivot table: bucket × project, value = days
    const allProjects = data.totals?.by_project || [];
    const headerRow = ["Bucket", ...allProjects.map((p) => p.project_name), "Total (days)"];
    lines.push(headerRow.map(csvEscape).join(","));
    for (const b of data.buckets) {
      const row = [b.label];
      for (const p of allProjects) {
        const found = b.projects.find((bp) => bp.project_id === p.project_id);
        row.push(found ? found.days.toFixed(2) : "0.00");
      }
      row.push(b.total_days.toFixed(2));
      lines.push(row.map(csvEscape).join(","));
    }
    // Total row
    const totalRow = ["Total"];
    for (const p of allProjects) totalRow.push(p.days.toFixed(2));
    totalRow.push((data.totals?.total_days || 0).toFixed(2));
    lines.push(totalRow.map(csvEscape).join(","));

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `effort-overview-${period}-${data.range?.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <div className="space-y-6" data-testid="effort-overview">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={(v) => { setPeriod(v); setCount(v === "month" ? 6 : 12); }}>
            <SelectTrigger className="w-[140px]" data-testid="effort-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(count)} onValueChange={(v) => setCount(parseInt(v))}>
            <SelectTrigger className="w-[180px]" data-testid="effort-count-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(period === "month" ? [3, 6, 12, 24] : [4, 8, 12, 26, 52]).map((n) => (
                <SelectItem key={n} value={String(n)}>
                  Last {n} {period === "month" ? "months" : "weeks"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOverview} disabled={loading} data-testid="effort-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={exportCSV} disabled={!data} data-testid="effort-export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Effort</p>
            <p className="text-2xl font-bold text-primary mt-1" data-testid="effort-total-days">
              {fmtDays(data?.totals?.total_days)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {fmtHours(data?.totals?.total_hours)} · 8h = 1 day
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Buckets</p>
            <p className="text-2xl font-bold mt-1">{data?.buckets?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {period === "month" ? "Months" : "Weeks"}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Active Projects</p>
            <p className="text-2xl font-bold mt-1">{data?.totals?.by_project?.length || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">In selected range</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Range</p>
            <p className="text-base font-medium mt-1 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-muted-foreground" />
              {data?.range?.start || "-"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              to {data?.range?.end || "-"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stacked bar chart by bucket */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Effort by {period === "month" ? "Month" : "Week"}</CardTitle>
          <CardDescription>Days spent per project (stacked). Top 8 projects shown individually, the rest grouped as &quot;Other&quot;.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground animate-pulse">Loading...</div>
          ) : chartData.length === 0 || projectKeysForChart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center text-muted-foreground">
              <Layers className="w-10 h-10 mb-3 opacity-50" />
              No timesheet entries in this range
            </div>
          ) : (
            <div className="h-80 min-h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={period === "week" ? -25 : 0} textAnchor={period === "week" ? "end" : "middle"} height={50} />
                  <YAxis tickFormatter={(v) => `${v}d`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v, name) => [fmtDays(v), name]}
                    labelStyle={{ color: "#2E5C46", fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {projectKeysForChart.map((key, i) => (
                    <Bar key={key} dataKey={key} stackId="a" fill={COLORS[i % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top projects (overall) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Total Effort by Project</CardTitle>
          <CardDescription>Sorted by days (highest first)</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(data?.totals?.by_project?.length || 0) === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">No data</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">Project</TableHead>
                  <TableHead className="table-header">Client</TableHead>
                  <TableHead className="table-header text-right">Days</TableHead>
                  <TableHead className="table-header text-right">Hours</TableHead>
                  <TableHead className="table-header text-right">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.totals.by_project.map((p) => {
                  const total = data.totals.total_days || 1;
                  const pct = (p.days / total) * 100;
                  return (
                    <TableRow key={p.project_id} className="hover:bg-accent/50" data-testid={`effort-project-row-${p.project_id}`}>
                      <TableCell className="font-medium">{p.project_name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.client_name || "-"}</TableCell>
                      <TableCell className="text-right data-cell font-medium">{fmtDays(p.days)}</TableCell>
                      <TableCell className="text-right data-cell text-muted-foreground">{fmtHours(p.hours)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{pct.toFixed(1)}%</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-bucket breakdown table (pivot preview) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{period === "month" ? "Monthly" : "Weekly"} Breakdown</CardTitle>
          <CardDescription>Days per project per {period}</CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {(data?.buckets?.length || 0) === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">No data</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header sticky left-0 bg-card">{period === "month" ? "Month" : "Week"}</TableHead>
                  {(data.totals.by_project || []).map((p) => (
                    <TableHead key={p.project_id} className="table-header text-right whitespace-nowrap">{p.project_name}</TableHead>
                  ))}
                  <TableHead className="table-header text-right whitespace-nowrap">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.buckets.map((b) => (
                  <TableRow key={b.label} className="hover:bg-accent/50" data-testid={`effort-bucket-row-${b.label}`}>
                    <TableCell className="font-medium sticky left-0 bg-card">
                      {b.label}
                      <p className="text-xs text-muted-foreground">{b.start} → {b.end}</p>
                    </TableCell>
                    {(data.totals.by_project || []).map((p) => {
                      const found = b.projects.find((bp) => bp.project_id === p.project_id);
                      return (
                        <TableCell key={p.project_id} className="text-right data-cell">
                          {found ? fmtDays(found.days) : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right data-cell font-semibold">{fmtDays(b.total_days)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default EffortOverview;
